<template>
  <f7-page
    name="create-campaign"
  >
    <f7-navbar back-link no-shadow>
      <f7-nav-title>
        {{ $t("campaignCreate.title") }}
      </f7-nav-title>
      <f7-nav-right>
        <span class="step-label">
          {{ $t("common.creationStep") }} {{ step }}/{{ totalSteps }} 
        </span>
        <!-- {{ stepName }} -->
      </f7-nav-right>
    </f7-navbar>
    <AppLayout>
      <template #main>
        <div class="step-bar">
          <div
            v-for="n in totalSteps"
            :key="n"
            class="step-dot"
            :class="{ on: n <= step }"
          />
        </div>

        <!-- Step 1: Family & Type -->
        <template v-if="step === 1">
          <f7-block strong inset class="section-header">
            <span class="step-badge">{{
              $t("campaignCreate.stepBadge", { n: 1, total: totalSteps })
            }}</span>
            <h2 class="section-title">
              {{ $t("campaignCreate.familyStep") }}
            </h2>
            <p class="section-subtitle">
              {{ $t("campaignCreate.familyStepIntro") }}
            </p>
          </f7-block>

          <CampaignFamilyGrid
            v-model="form.type"
            radio-name="campaign_family"
          />
        </template>

        <!-- Step 2: Fast Draft Basics -->
        <template v-if="step === 2">
          <f7-block strong inset class="section-header">
            <span class="step-badge">{{
              $t("campaignCreate.stepBadge", { n: 2, total: totalSteps })
            }}</span>
            <h2 class="section-title">
              {{ $t("campaignCreate.basicsStep") }}
            </h2>
            <p class="section-subtitle">
              {{ $t("campaignCreate.basicsStepIntro") }}
            </p>
          </f7-block>

          <f7-list inset strong dividers outline media-list >
            <f7-list-input
              :label="$t('campaign.CampaignName')"
              type="text"
              required
              :value="form.name"
              @input="form.name = $event.target.value"
              :placeholder="$t('campaignCreate.namePlaceholder')"
              clear-button
            />

            <f7-list-input
              :label="$t('common.description')"
              :placeholder="$t('campaignCreate.descPlaceholder')"
              :value="form.description"
              @texteditor:change="(v) => (form.description = v)"
              resizable
              required
              type="texteditor"
              :text-editor-params="{
                mode: 'keyboard-toolbar',
              }"
              :style="
                Capacitor.isNativePlatform() || !isTablet
                  ? '--f7-text-editor-height: 200px'
                  : ''
              "
            />

            <!-- Type-specific goal field -->
            <f7-list-input
              v-if="goalType === 'amount'"
              :label="$t('campaignCreate.targetAmountLabel')"
              type="text"
              inputmode="numeric"
              :value="formatMoneyInput(form.targetAmount)"
              @input="form.targetAmount = parseMoney($event.target.value)"
              placeholder="200 000"
              input-clear-button
            >
              <template #media>
                <span
                  style="padding-right: 5px; color: var(--f7-theme-color)"
                  >{{
                    groupCurrency
                  }}</span
                >
              </template>
              <template #after>
                <span class="input-clear-button">{{
                  groupCurrency
                }}</span>
              </template>
            </f7-list-input>
            <div v-if="goalType === 'amount'" class="preset-chips">
              <span class="font-label-sm text-on-surface-variant">{{
                $t("campaignCreate.amountPresetsHint")
              }}</span>
              <f7-button
                v-for="preset in amountPresets"
                :key="preset"

                round
                small
                :fill="form.targetAmount === preset"
                :class="
                  form.targetAmount === preset
                    ? 'shadow-xs'
                    : 'bg-surface-container-high text-on-surface'
                "
                @click="form.targetAmount = preset"
              >
                {{ Number(preset).toLocaleString(navigator?.languages) }}
                {{ groupCurrency }}
              </f7-button>
            </div>

            <f7-list-input
              v-if="goalType === 'quantity'"
              :label="$t('common.TargetQuantity')"
              type="number"
              :value="form.targetQuantity"
              @input="form.targetQuantity = $event.target.value"
            />

            <f7-list-input
              v-if="goalType === 'participants'"
              :label="$t('common.MaxParticipants')"
              type="number"
              :value="form.maxParticipants"
              @input="form.maxParticipants = $event.target.value"
            />

            <f7-list-item
              :title="$t('common.Deadline')"
              smart-select
              @smartselect:close="closeDeadLine()"
              :smart-select-params="{
                openIn: isTablet ? 'popover' : 'sheet',
                closeOnSelect: true,
                sheetSwipeToClose: true,
                dateFormat: 'dd MM yyyy',
              }"
            >
              <select v-model="form.deadline">
                <option value="">{{ $t("common.noDeadline") }}</option>
                <option value="7d">{{ $t("common.deadline.7d") }}</option>
                <option value="14d">{{ $t("common.deadline.14d") }}</option>
                <option value="30d">{{ $t("common.deadline.30d") }}</option>
                <option value="90d">{{ $t("common.deadline.90d") }}</option>
                <option value="180">{{ $t("common.deadline.180d") }}</option>
                <option value="custom">{{ $t("common.deadline.365d") }}</option>
              </select>
            </f7-list-item>

            <f7-list-input
              v-if="form.deadline === 'custom'"
              :label="$t('common.Deadline')"
              type="datepicker"
              readonly
              :value="form.deadlineDate"
              :placeholder="$t('common.deadline.365d')"
              :calendar-params="{
                closeOnSelect: true,
                minDate: new Date(),
                header: true,
                footer: true,
                openIn: 'auto',
                dateFormat: 'dd MM yyyy',
              }"
              clear-button
              @calendar:change="(dates) => (form.deadlineDate = dates)"
            />
          </f7-list>

          <!-- Reassurance card -->
          <f7-block strong inset class="reassurance-card">
            <div class="reassurance-content">
              <div class="reassurance-icon">
                <span
                  class="material-symbols-outlined"
                  style="font-size: 22px; font-variation-settings: 'FILL' 1"
                  >shield_with_heart</span
                >
              </div>
              <div class="reassurance-text">
                <div class="reassurance-title">
                  {{ $t("campaignCreate.reassuranceTitle") }}
                </div>
                <p class="reassurance-body">
                  {{ $t("campaignCreate.reassuranceText") }}
                </p>
              </div>
            </div>
          </f7-block>
        </template>

        <!-- Step 3: Audience & Start Mode -->
        <template v-if="step === 3">
          <f7-block strong inset class="section-header">
            <span class="step-badge">{{
              $t("campaignCreate.stepBadge", { n: 3, total: totalSteps })
            }}</span>
            <h2 class="section-title">
              {{ $t("campaignCreate.audienceStep") }}
            </h2>
            <p class="section-subtitle">
              {{ $t("campaignCreate.audienceStepIntro") }}
            </p>
          </f7-block>

          <f7-block inset class="no-padding-horizontal">
            <div class="audience-label">
              {{ $t("campaignCreate.audienceLabel") }}
            </div>
            <div class="audience-options">
              <label
                class="audience-option radio"
                :class="{ selected: form.audience === 'GROUP_MEMBERS' }"
              >
                <div class="audience-content margin-left-half">
                  <div class="audience-icon">
                    <f7-icon material="groups" :size="18" />
                  </div>
                  <div class="audience-text">
                    <div class="audience-row">
                      <span class="audience-title">{{ $t("campaignCreate.audienceGroup") }}</span>
                      <span class="audience-badge">{{ $t("campaignCreate.audienceGroupBadge") }}</span>
                    </div>
                    <span class="audience-desc">{{ $t("campaignCreate.audienceGroupDesc") }}</span>
                  </div>
                </div>
                <input
                  type="radio"
                  class="radio-input"
                  name="campaign_audience"
                  value="GROUP_MEMBERS"
                  :checked="form.audience === 'GROUP_MEMBERS'"
                  @change="form.audience = 'GROUP_MEMBERS'"
                />
                <i class="icon-radio"></i>
              </label>

              <label
                class="audience-option radio"
                :class="{ selected: form.audience === 'LINK_ONLY' }"
              >
                <div class="audience-content margin-left-half">
                  <div class="audience-icon audience-icon-secondary">
                    <f7-icon material="link" :size="18" />
                  </div>
                  <div class="audience-text">
                    <div class="audience-row">
                      <span class="audience-title">{{ $t("campaignCreate.audienceLink") }}</span>
                      <span class="audience-badge audience-badge-secondary">{{ $t("campaignCreate.audienceLinkBadge") }}</span>
                    </div>
                    <span class="audience-desc">{{ $t("campaignCreate.audienceLinkDesc") }}</span>
                  </div>
                </div>
                <input
                  type="radio"
                  class="radio-input"
                  name="campaign_audience"
                  value="LINK_ONLY"
                  :checked="form.audience === 'LINK_ONLY'"
                  @change="form.audience = 'LINK_ONLY'"
                />
                <i class="icon-radio"></i>
              </label>

              <label
                class="audience-option radio"
                :class="{ selected: form.audience === 'PUBLIC' }"
              >
                <div class="audience-content">
                  <div class="audience-icon audience-icon-tertiary margin-left-half">
                    <f7-icon material="public" :size="18" />
                  </div>
                  <div class="audience-text">
                    <div class="audience-row">
                      <span class="audience-title">{{ $t("campaignCreate.audiencePublic") }}</span>
                      <span class="audience-badge audience-badge-tertiary">{{ $t("campaignCreate.audiencePublicBadge") }}</span>
                    </div>
                    <span class="audience-desc">{{ $t("campaignCreate.audiencePublicDesc") }}</span>
                  </div>
                </div>
                <input
                  type="radio"
                  class="radio-input"
                  name="campaign_audience"
                  value="PUBLIC"
                  :checked="form.audience === 'PUBLIC'"
                  @change="form.audience = 'PUBLIC'"
                />
                <i class="icon-radio"></i>
              </label>
            </div>
          </f7-block>

          <f7-block strong inset class="no-margin-top">
            <div class="start-mode-label">
              {{ $t("campaignCreate.startModeLabel") }}
            </div>
            <f7-segmented round>
              <f7-button
                round
                raised
                large
                :active="form.startMode === 'draft'"
                @click="form.startMode = 'draft'"
                >{{ $t("campaignCreate.startDraft") }}</f7-button
              >
              <f7-button
                round
                raised
                :active="form.startMode === 'now'"
large                @click="form.startMode = 'now'"
                :disabled="!readiness.ready"
                >{{ $t("campaignCreate.startNow") }}</f7-button
              >
            </f7-segmented>
            <p
              class="start-mode-hint"
              :class="{
                'text-primary': form.startMode === 'now' && readiness.ready,
                'text-error': form.startMode === 'now' && !readiness.ready,
                'text-on-surface-variant': form.startMode === 'draft',
              }"
            >
              {{
                form.startMode === "now"
                  ? readiness.ready
                    ? $t("campaignCreate.startNowDesc")
                    : $t("campaignCreate.startNowBlocked")
                  : $t("campaignCreate.startDraftDesc")
              }}
            </p>
          </f7-block>

          <f7-block strong inset v-if="form.startMode === 'now'">
            <div class="readiness-header">
              <f7-icon
                :f7="
                  readiness.ready
                    ? 'checkmark_shield_fill'
                    : 'exclamationmark_shield_fill'
                "
                size="20"
                :color="readiness.ready ? 'green' : 'orange'"
              />
              <span class="readiness-title">{{
                readiness.ready
                  ? $t("campaignCreate.readyToPublish")
                  : $t("campaignCreate.notReadyToPublish")
              }}</span>
            </div>
            <div
              v-if="readiness?.blockers?.length"
              class="readiness-blockers"
            >
              <p class="readiness-section-title text-error">
                {{ $t("campaignCreate.missingRequirements") }}
              </p>
              <ul>
                <li
                  v-for="(b, i) in readiness.blockers"
                  :key="i"
                   class="readiness-item"
                 >
                  {{ b }}
                </li>
              </ul>
            </div>
            <div
              v-if="readiness?.warnings?.length"
              class="readiness-warnings"
            >
              <p class="readiness-section-title text-secondary">
                {{ $t("campaignCreate.warnings") }}
              </p>
              <ul>
                <li
                  v-for="(w, i) in readiness.warnings"
                  :key="i"
                   class="readiness-item"
                 >
                  {{ w }}
                </li>
              </ul>
            </div>
          </f7-block>
        </template>

<!-- Bottom Actions -->
        <f7-toolbar bottom class="create-footer no-hairlines">
          <div :class="['create-footer-inner', { 'with-back': step > 1 }]">
            <f7-button  class="create-footer-back"
              v-if="step > 1"
              outline
              raised
              large
              round
              @click="step--"
            >
              {{ $t("common.back") }}
            </f7-button>
            <f7-button
              class="create-footer-primary"
              fill
              large
              round
              raised
              :disabled="!canContinue"
              @click="step < totalSteps ? step++ : submit()"
            >
              {{
                step < totalSteps
                  ? $t("group.creationNextStep")
                  : form.startMode === "now"
                  ? $t("campaignCreate.publishNowCta")
                  : $t("campaignCreate.createDraftCta")
              }}
            </f7-button>
          </div>
        </f7-toolbar>
      </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { reactive, ref, computed, watch, onMounted } from "vue";
import _ from "lodash";
import { f7, f7ready } from "framework7-vue";
import { useI18n } from "vue-i18n";
import CampaignFamilyGrid from "../components/campaign/CampaignFamilyGrid.vue";
import AppLayout from "../components/common/AppLayout.vue";

import { capsFor, metaFor } from "../constants/campaign-capabilities.js";
import { useCampaignsStore } from "../js/pinia-stores/campaign.js";
import { useGroupsStore } from "../js/pinia-stores/group.js";
import { useKeycloakStore } from "../js/pinia-stores/keycloak.js";
import { useAuthStore } from "../js/pinia-stores/auth.js";
import { useLargeScreen } from "../js/composables/useMediaQuery.js";
import { Capacitor } from "@capacitor/core";

const { t } = useI18n();
const props = defineProps({
  groupId: { type: String, required: true },
  group: { type: Object, required: false, default: null },
});

const campaignsStore = useCampaignsStore();
const groupsStore = useGroupsStore();
const keycloak = useKeycloakStore();
const authStore = useAuthStore();
const step = ref(1);
const isTablet = useLargeScreen().matches;
const readiness = ref({ ready: false, blockers: [], warnings: [] });
const totalSteps = 3;

// Use group from props or fetch from store if not provided
const group = computed(() => {
  if (props.group) return props.group;
  return groupsStore.activeGroup;
});

// Fetch group if not provided via props
onMounted(async () => {
  if (!props.group && props.groupId) {
    await groupsStore.fetchGroupDetail(props.groupId);
  }
});

// Draft autosave v2
const DRAFT_KEY = computed(
  () => `avocash:create-campaign:draft:v2:${props.groupId}`,
);
function persistDraft() {
  try {
    localStorage.setItem(
      DRAFT_KEY.value,
      JSON.stringify({
        step: step.value,
        savedAt: Date.now(),
        form: { ...form },
        version: 2,
      }),
    );
  } catch (e) {
    /* ignore */
  }
}
const saveDraft = _.debounce(persistDraft, 800);

function restoreDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY.value);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!parsed?.form || parsed.version !== 2) return;
    // Only copy known properties to avoid polluting form with old draft fields
    const allowedKeys = [
      "type",
      "name",
      "description",
      "targetAmount",
      "targetQuantity",
      "maxParticipants",
      "deadline",
      "deadlineDate",
      "audience",
      "startMode",
    ];
    for (const key of allowedKeys) {
      if (key in parsed.form) form[key] = parsed.form[key];
    }
    if (parsed.step)
      step.value = Math.min(Math.max(1, Number(parsed.step)), totalSteps);
  } catch (e) {
    /* ignore */
  }
}

function clearDraft() {
  try {
    localStorage.removeItem(DRAFT_KEY.value);
  } catch (e) {
    /* ignore */
  }
}

const form = reactive({
  type: null,
  name: "",
  description: "",
  targetAmount: "",
  currency: "",
  targetQuantity: "",
  maxParticipants: "",
  deadline: "",
  deadlineDate: "",
  audience: "GROUP_MEMBERS",
  startMode: "draft",
});

// Initialize currency when group is available
watch(group, (newGroup) => {
  if (newGroup && !form.currency) {
    form.currency = newGroup.currency || newGroup.defaultCurrency;
  }
}, { immediate: true });

const amountPresets = ["100000", "200000", "500000", "1000000"];
const goalType = computed(() => metaFor(form.type)?.goal || "none");
const caps = computed(() => capsFor(form.type));

// Group currency helper
const groupCurrency = computed(() => group.value?.currency || group.value?.defaultCurrency || "CFA");

const canContinue = computed(() => {
  if (step.value === 1) return !!form.type;
  if (step.value === 2)
    return (
      form.name.trim().length >= 2 &&
      (goalType.value === "none" ||
        (form.targetAmount && Number(form.targetAmount) > 0))
    );
  if (step.value === 3) return true;
  return true;
});

const stepName = computed(() => {
  if (step.value === 1) return t("campaignCreate.familyStep");
  if (step.value === 2) return t("campaignCreate.basicsStep");
  return t("campaignCreate.audienceStep");
});

function checkReadiness() {
  const blockers = [];
  const warnings = [];
  if (!form.type) {
    blockers.push(t("campaignCreate.typeRequired"));
    readiness.value = { ready: false, blockers, warnings };
    return;
  }
  if (!form.name?.trim()) blockers.push(t("campaignCreate.nameRequired"));
  const typeCaps = caps.value;
  if (
    typeCaps.contributions &&
    (!form.targetAmount || Number(form.targetAmount) <= 0)
  ) {
    blockers.push(t("campaignCreate.targetAmountRequired"));
  }
  if (typeCaps.contributions && !form.currency)
    warnings.push(t("campaignCreate.currencyRequired"));
  readiness.value = { ready: blockers.length === 0, blockers, warnings };
}

function parseMoney(value) {
  return String(value || "").replace(/[^0-9]/g, "");
}
function formatMoneyInput(value) {
  return value
    ? new Intl.NumberFormat(navigator?.languages).format(Number(value))
    : "";
}

function endOfDayISO(value) {
  const raw = Array.isArray(value) ? value[0] : value;
  if (!raw) return null;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      23,
      59,
      59,
      999,
    ),
  ).toISOString();
}

function computeDeadline() {
  if (form.deadline === "custom") return endOfDayISO(form.deadlineDate);
  if (!form.deadline) return null;
  const days = Number(String(form.deadline).replace("d", ""));
  if (!Number.isFinite(days) || days <= 0) return null;
  return endOfDayISO(new Date(Date.now() + days * 86_400_000));
}

function confirmLaunchNow() {
  return new Promise((resolve) => {
    f7.dialog.confirm(
      t("campaignCreate.launchNowConfirmText"),
      t("campaignCreate.launchNowConfirmTitle"),
      () => resolve(true),
      () => resolve(false),
    );
  });
}

async function submit() {
  if (form.startMode === "now" && !(await confirmLaunchNow())) return;
  f7.preloader.show();
  try {
    const config = { audience: form.audience };
    const campaign = await campaignsStore.createCampaign({
      groupId: props.groupId,
      createdBy: keycloak.subject,
      type: form.type,
      name: form.name,
      description: form.description,
      banner: null,
      logo: null,
      documents: [],
      targetAmount: form.targetAmount ? Number(form.targetAmount) : null,
      targetQuantity: form.targetQuantity ? Number(form.targetQuantity) : null,
      maxParticipants: form.maxParticipants
        ? Number(form.maxParticipants)
        : null,
      deadline: computeDeadline(),
      items: null,
      cycles: null,
      operatingCountry: group.value?.countryCode,
      settlementCurrency: form.currencyCode,
      displayCurrency: form.currencyCode,
      fulfilmentLocation: null,
      config,
    });
    if (form.startMode === "now") {
      await campaignsStore.publishCampaign(campaign.id);
    }
    f7.preloader.hide();
    clearDraft();
    f7.toast.show({
      text:
        form.startMode === "now"
          ? t("campaignCreate.launched")
          : t("campaignCreate.savedDraft"),
      closeTimeout: 1800,
    });
    if (form.type === "TONTINE") {
      f7.router.navigate(
        `/groups/${props.groupId}/campaigns/${campaign.id}/tontine/setup/`,
      );
      return;
    }
    f7.router.navigate(`/groups/${props.groupId}/campaigns/${campaign.id}`);
  } catch (e) {
    f7.preloader.hide();
    f7.dialog.alert(
      e?.response?.data?.message || e?.message || t("common.creationFailed"),
    );
  }
}

function closeDeadLine() {
  if (!form.deadline || form.deadline === "custom") form.deadlineDate = "";
}

watch(() => JSON.stringify({ step: step.value, form }), saveDraft, {
  deep: true,
});
watch(() => form.type, checkReadiness);
watch(() => form.targetAmount, checkReadiness);
watch(() => form.name, checkReadiness);

onMounted(() => {
  restoreDraft();
  if (form.type) checkReadiness();
  // Restore toast if draft was restored. Defer until the Framework7 app is
  // initialized (f7.$el is set), otherwise f7.toast.show throws during the
  // initial app mount when this page is loaded directly.
  f7ready(() => {
    if (form.name && step.value > 1) {
      f7.toast.show({
        text: t("campaignCreate.draftRestored"),
        closeTimeout: 2000,
      });
    }
  });
});
</script>

<style scoped>
.section-header {
  margin-bottom: 12px;
}
.step-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 9999px;
  background: var(--f7-theme-color);
  color: white;
  text-transform: uppercase;
  margin-bottom: 8px;
}
.step-bar {
  display: flex;
  gap: 4px;
  padding: 8px 16px 0;
}
.step-dot {
  flex: 1;
  height: 3px;
  border-radius: 2px;
  background: var(--f7-list-item-border-color, #e0e0e0);
}
.step-dot.on {
  background: var(--f7-theme-color);
}
.preset-chips button {
  white-space: nowrap;
  flex-shrink: 0;
}
.reassurance-card {
  background: var(--f7-surface-container, #f5f5f5);
  border-radius: 12px;
  border: 1px solid var(--f7-list-item-border-color, #e0e0e0);
}
.audience-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.audience-option.radio {
  cursor: pointer;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s ease;
  display: flex;
  align-items: flex-start;
  padding: 0;
  background: var(--f7-card-bg-color, #fff);
  border: 1px solid var(--f7-list-item-border-color, #e0e0e0);
}

.audience-option.radio:has(input:checked) {
  border-color: var(--f7-theme-color);
  background: color-mix(in srgb, var(--f7-theme-color) 6%, transparent);
}

.audience-option.radio:hover {
  background: var(--f7-surface-container-high, #f5f5f5);
}

.audience-option.radio .radio-input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.audience-option.radio .icon-radio {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin: 16px;
  flex-shrink: 0;
  border: 2px solid var(--f7-list-item-border-color, #e0e0e0);
  border-radius: 50%;
  background: var(--f7-card-bg-color, #fff);
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.audience-option.radio .icon-radio::after {
  content: "";
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--f7-theme-color);
  opacity: 0;
  transform: scale(0);
  transition: all 0.2s ease;
}

.audience-option.radio input:checked ~ .icon-radio {
  border-color: var(--f7-theme-color);
}

.audience-option.radio input:checked ~ .icon-radio::after {
  opacity: 1;
  transform: scale(1);
}

.audience-option.radio .audience-content {
  flex: 1;
  padding: 14px 16px 14px 0;
  min-width: 0;
}
.start-mode-hint {
  line-height: 1.4;
  font-size: 12px;
  margin-top: 8px;
}
.readiness-blockers,
.readiness-warnings {
  margin-top: 12px;
}
.readiness-section-title {
  font-size: var(--f7-label-sm-font-size);
  font-weight: var(--f7-label-sm-font-weight);
}
.readiness-blockers ul,
.readiness-warnings ul {
  margin-top: 4px;
  padding-left: 20px;
}
.readiness-item {
  font-size: var(--f7-body-sm-font-size);
  color: var(--f7-on-surface-variant);
  line-height: 1.6;
  margin: 2px 0;
}
.readiness-summary {
  background: color-mix(in srgb, var(--f7-theme-color) 6%, transparent);
  border: 1px solid color-mix(in srgb, var(--f7-theme-color) 16%, transparent);
  border-radius: 12px;
}
.readiness-header {
  font-size: 14px;
}
.readiness-blockers ul,
.readiness-warnings ul {
  margin: 0;
  padding-left: 18px;
}
.readiness-blockers li,
.readiness-warnings li {
  font-size: 12px;
  line-height: 1.6;
  margin: 2px 0;
}
/* Stretch the main column to the full viewport height (minus navbar)
   so the footer is pushed to the bottom of the page. */
:deep(.main_layout) {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - var(--f7-navbar-height, 56px));
  min-height: calc(100dvh - var(--f7-navbar-height, 56px));
}

.create-footer {
  position: sticky;
  bottom: 0;
  margin-top: auto;
  margin-bottom: 0;
  padding: 12px 16px calc(12px + var(--f7-safe-area-bottom, 0px));
  background: transparent;
  /* linear-gradient(
    to top,
    var(--f7-page-bg-color, #fff),
    transparent
  ); */
  z-index: 10;
}
.step-label {
  font-size: var(--f7-label-sm-font-size);
  font-weight: var(--f7-label-sm-font-weight);
  color: var(--f7-theme-color);
}
@media (min-width: 1200px) {
  .create-footer {
    position: static;
    background: none;
  }
}
.section-title {
  font-size: var(--f7-headline-sm-font-size);
  font-weight: var(--f7-headline-sm-font-weight);
  color: var(--f7-on-surface);
  margin: 0;
}
.section-subtitle {
  font-size: var(--f7-body-sm-font-size);
  color: var(--f7-on-surface-variant);
  margin-top: 4px;
  margin-bottom: 0;
}
.preset-chips {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  overflow-x: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.preset-chips::-webkit-scrollbar {
  display: none;
}
.preset-hint {
  font-size: var(--f7-label-sm-font-size);
  color: var(--f7-on-surface-variant);
  white-space: nowrap;
}
.preset-chips .button {
  white-space: nowrap;
  flex-shrink: 0;
}
.reassurance-content {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.reassurance-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: color-mix(in srgb, var(--f7-theme-color) 10%, transparent);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: var(--f7-theme-color);
  margin-top: 2px;
}
.reassurance-text {
  flex: 1;
}
.reassurance-title {
  font-size: var(--f7-label-md-font-size);
  font-weight: var(--f7-label-md-font-weight);
  color: var(--f7-on-surface);
}
.reassurance-body {
  font-size: var(--f7-body-sm-font-size);
  color: var(--f7-on-surface-variant);
  margin-top: 4px;
  margin-bottom: 0;
}
.audience-label {
  font-size: var(--f7-label-md-font-size);
  font-weight: var(--f7-label-md-font-weight);
  color: var(--f7-on-surface);
  margin-bottom: 12px;
}
.audience-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.audience-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  flex: 1;
}
.audience-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--f7-theme-color);
  color: var(--f7-on-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.audience-icon-secondary {
  background: var(--f7-secondary-container);
  color: var(--f7-on-secondary-container);
}
.audience-icon-tertiary {
  background: var(--f7-tertiary-container);
  color: var(--f7-on-tertiary-container);
}
.audience-text {
  flex: 1;
  min-width: 0;
}
.audience-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.audience-title {
  font-size: var(--f7-body-md-font-size);
  font-weight: 600;
  color: var(--f7-on-surface);
}
.audience-badge {
  background: var(--f7-primary-container);
  color: var(--f7-on-primary-container);
  font-size: var(--f7-label-sm-font-size);
  padding: 2px 8px;
  border-radius: 9999px;
  font-weight: 700;
  text-transform: uppercase;
}
.audience-badge-secondary {
  background: var(--f7-secondary-container);
  color: var(--f7-on-secondary-container);
}
.audience-badge-tertiary {
  background: var(--f7-tertiary-container);
  color: var(--f7-on-tertiary-container);
}
.audience-desc {
  font-size: var(--f7-body-sm-font-size);
  color: var(--f7-on-surface-variant);
  margin-top: 4px;
  display: block;
}
.start-mode-label {
  font-size: var(--f7-label-md-font-size);
  font-weight: var(--f7-label-md-font-weight);
  color: var(--f7-on-surface);
  margin-bottom: 12px;
}
.readiness-header {
  display: flex;
  align-items: center;
  gap: 8px;
}
.readiness-title {
  font-size: var(--f7-label-lg-font-size);
  font-weight: var(--f7-label-lg-font-weight);
}
.create-footer-inner {
  display: flex;
  width: 100%;
  gap: 8px;
}
.create-footer-inner .create-footer-primary {
  flex: 1;
}
.create-footer-inner.with-back .create-footer-back {
  flex: 0 0 25%;
}
</style>

